'use client';

import { useState } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';

import GamesSwiper from './GamesSwiper';
import GamesFiltersSwiper from './GamesFiltersSwiper';
import { GamesFilter, GamesFilterKey, SportsSubcategory } from '@/types/Game';
import { Typography } from '@/components';

import { HeroBackgroundGradientDesktop, HeroBackgroundGradientXL, HeroBackgroundMobile } from '@/assets';

import { games as allGames } from './games';
import GamesSportsFilter from './GamesSportsFilter';

const FILTERS: GamesFilter[] = [
  {
    key: 'all',
    label: 'All',
    type: null,
  },
  {
    key: 'sports',
    label: 'Sports',
    type: 'F2P',
  },
  {
    key: 'casino',
    label: 'Casino',
    type: 'F2P',
  },
  {
    key: 'jackpots',
    label: 'Jackpots',
    type: null,
  },
];

const Hero = () => {
  const [gamesImagesLoaded, setGamesImagesLoaded] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<GamesFilterKey>('all');
  const [selectedSportsFilter, setSelectedSportsFilter] = useState<SportsSubcategory | null>(null);

  const filterAnimationDuration = typeof window !== 'undefined' && window.innerWidth > 600 ? 0.6 : 0.4;
  const sportsFilterTransition = { duration: 0.7, ease: 'easeInOut' };

  const handleGameImagesLoaded = () => {
    if (gamesImagesLoaded) {
      return;
    }

    setGamesImagesLoaded(true);
  };

  return (
    <section className='relative w-full h-full pt-[160px] md:pt-[130px] flex flex-col items-center pb-[120px] md:pb-[60px] overflow-visible'>
      <Image
        className='absolute object-center object-cover mx-auto z-0 block 2xl:hidden'
        src={HeroBackgroundGradientXL}
        fill
        priority
        alt=' '
      />
      <Image
        className='absolute object-center object-cover mx-auto z-0 hidden 2xl:block md:hidden'
        src={HeroBackgroundGradientDesktop}
        fill
        priority
        alt=' '
      />
      <Image
        className='absolute object-center object-cover mx-auto z-0 w-full hidden md:block'
        src={HeroBackgroundMobile}
        fill
        priority
        alt=' '
      />
      <div className='text-neutral-0 relative max-w-[800px] text-center px-5'>
        <Typography tag='h1' size='3xl' className='font-bold'>
          <span className='inline md:block'>Sticky Free Games.</span>{' '}
          <span className='inline md:block text-secondary'>Powerful Real-Money Jackpots.</span>
        </Typography>
      </div>

      <div className='w-full relative mt-5'>
        <GamesFiltersSwiper filters={FILTERS} onChange={setSelectedFilter} selectedFilter={selectedFilter} />
      </div>

      <div className='relative h-full w-full z-0'>
        <GamesSwiper
          games={allGames}
          onImagesLoaded={handleGameImagesLoaded}
          filters={FILTERS}
          selectedFilter={selectedFilter}
          filterAnimationDuration={filterAnimationDuration}
          selectedSportFilter={selectedSportsFilter}
        />

        <motion.div
          className='absolute top-[80px] right-[20px] sm:top-[140px] sm:right-[6px] pr-4 z-20'
          initial={{ opacity: 0, scale: 0.9 }}
          animate={selectedFilter === 'sports' ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
          transition={{
            opacity: { ...sportsFilterTransition, delay: selectedFilter === 'sports' ? filterAnimationDuration : 0 },
            scale: { ...sportsFilterTransition, delay: selectedFilter === 'sports' ? filterAnimationDuration : 0 },
          }}>
          <GamesSportsFilter selectedSport={selectedSportsFilter} onChange={setSelectedSportsFilter} />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
